import { Domain } from '@hypervision/models/domain';
import { forEach, isDefined, isUndefined } from 'angular';

import { StorageService } from '@contact/upgrade';

export interface ISkillViewFilterService {
  // Is a skill must be shown for the selected media filter
  isSkillApplicableToMedia(skill, mediaAccessor);
  filterApplicableSkillsForMedia(skills, mediaAccessor);
  // Apply filter to registered datasets
  applyFilter(viewName);
  getStorageFilterKey(viewName);
  saveFilter(viewName);
  getSavedFilter(viewName);
  // Register a filter
  registerFilter(viewName, dataSet, cols, mediaAccessor, allSkills, allDomains: Domain[]);
  // Register a filter
  unregisterFilter();
  getFilter(viewName);
  // Called when an indicator is added / removed
  notifyIndicatorChange(viewName, indicatorLabel, selected);
  // Called when the user changes the ledia
  notifyMediaChange(viewName, mediaAccessor, applyFilterNow);
  // Called when the domain changes
  notifyDomainChange(viewName, domainUuids);
  // Called when the skill changes to an other media
  notifySkillChange(viewName, skillUuids, dontSave?: boolean);
  isFilterActive(viewName);
}

export class SkillViewFilterService implements ISkillViewFilterService {
  static $inject: Array<string> = ['SKILL_METRIC_NAMES', 'storage'];

  private filters: any = {};

  constructor(private readonly SKILL_METRIC_NAMES, private readonly storage: StorageService) {}

  // Finds an indicator by its label
  private findIndicatorByLabel(label) {
    let i;
    for (i = 0; i < this.SKILL_METRIC_NAMES.length; i = i + 1) {
      if (this.SKILL_METRIC_NAMES[i].label === label) {
        return this.SKILL_METRIC_NAMES[i];
      }
    }
    return undefined;
  }

  // Return an skill stored in the filter by its uuid
  private getSkill(filter, uuid) {
    if (isDefined(filter.allSkills)) {
      let i;
      for (i = 0; i < filter.allSkills.length; i = i + 1) {
        if (filter.allSkills[i].uuid === uuid) {
          return filter.allSkills[i];
        }
      }
    }
    return undefined;
  }

  // Is a skill must be shown for the selected media filter
  isSkillApplicableToMedia(skill, mediaAccessor) {
    let i;
    if (mediaAccessor === 'incomingPhoneName') {
      for (i = 0; i < skill.channels.length; i = i + 1) {
        if (skill.channels[i].direction === 'INCOMING' && skill.channels[i].mediaType === 'PHONE') {
          return true;
        }
      }
    } else if (mediaAccessor === 'outgoingPhoneName') {
      for (i = 0; i < skill.channels.length; i = i + 1) {
        if (skill.channels[i].direction === 'OUTGOING' && skill.channels[i].mediaType === 'PHONE') {
          return true;
        }
      }
    } else if (mediaAccessor === 'mailName') {
      for (i = 0; i < skill.channels.length; i = i + 1) {
        if (skill.channels[i].mediaType === 'MAIL') {
          return true;
        }
      }
    } else if (mediaAccessor === 'chatIadvizeName') {
      for (i = 0; i < skill.channels.length; i = i + 1) {
        if (skill.channels[i].mediaType === 'CHAT_IADVIZE') {
          return true;
        }
      }
    } else {
      return true;
    }
    return undefined;
  }

  filterApplicableSkillsForMedia(skills, mediaAccessor) {
    let filteredSkills: any[] = [];
    forEach(skills, skill => {
      if (this.isSkillApplicableToMedia(skill, mediaAccessor)) {
        filteredSkills.push(skill);
      }
    });
    return filteredSkills;
  }

  // Apply filter to registered datasets
  applyFilter(viewName) {
    let currentFilter = this.filters[viewName];
    if (isDefined(currentFilter)) {
      // filter rows
      forEach(currentFilter.rows, row => {
        let rowSkill = this.getSkill(currentFilter, row.skillUuid);
        row.show =
          currentFilter.domainUuids.indexOf(row.domainUuid) >= 0 &&
          currentFilter.skillUuids.indexOf(row.skillUuid) >= 0 &&
          this.isSkillApplicableToMedia(rowSkill, currentFilter.mediaAccessor);
      });
      // filter columns
      forEach(currentFilter.columns, column => {
        if (isDefined(currentFilter.indicators[column.label])) {
          column.show =
            currentFilter.indicators[column.label] && isDefined(this.findIndicatorByLabel(column.label)[currentFilter.mediaAccessor]);
        } else {
          column.show = true;
        }
      });
    }
  }

  getStorageFilterKey(viewName) {
    return viewName + '-filter';
  }

  saveFilter(viewName) {
    let currentFilter = this.filters[viewName];
    let filterSavedObject = {
      domainUuids: currentFilter.domainUuids,
      skillUuids: currentFilter.skillUuids,
      indicators: currentFilter.indicators,
    };
    this.storage.store(this.getStorageFilterKey(viewName), filterSavedObject, false);
  }

  getSavedFilter(viewName) {
    return this.storage.get(this.getStorageFilterKey(viewName), false).getOrElse({});
  }

  // Initialise rows
  private initRows(filter) {
    // Init the filter according to the current dataSet row visibility
    forEach(filter.rows, row => {
      if (filter.domainUuids.indexOf(row.domainUuid) < 0) {
        filter.domainUuids.push(row.domainUuid);
      }
      if (filter.skillUuids.indexOf(row.skillUuid) < 0) {
        filter.skillUuids.push(row.skillUuid);
      }
    });
  }

  // Initialise columns
  private initCols(filter) {
    // Init the filter according to the current dataSet column visibility
    forEach(this.SKILL_METRIC_NAMES, indicator => {
      filter.indicators[indicator.label] = true;
    });
  }

  private copyColumnVisibility(srcCols, destCols) {
    let s;
    let d;
    for (s = 0; s < srcCols.length; s = s + 1) {
      for (d = 0; d < destCols.length; d = d + 1) {
        if (srcCols[s].label === destCols[d].label) {
          destCols[d].show = srcCols[s].show;
        }
      }
    }
  }

  // Register a filter
  registerFilter(viewName, dataSet, cols, mediaAccessor, allSkills, allDomains: Domain[]) {
    let filter;
    let storedFilter;
    storedFilter = this.getSavedFilter(viewName);
    if (isDefined(storedFilter) && isDefined(storedFilter.domainUuids)) {
      filter = storedFilter;
      filter.mediaAccessor = mediaAccessor;
      filter.rows = dataSet;
      filter.columns = cols;
      filter.allSkills = allSkills;
      filter.allDomains = allDomains;
      this.filters[viewName] = filter;
      this.copyColumnVisibility(filter.columns, cols);
    } else if (!this.filters.hasOwnProperty(viewName)) {
      filter = {
        domainUuids: [],
        skillUuids: [],
        indicators: {},
        mediaAccessor: mediaAccessor,
        rows: dataSet,
        columns: cols,
        allSkills: allSkills,
        allDomains: allDomains,
      };
      this.filters[viewName] = filter;
      this.initCols(filter);
      this.initRows(filter);
    } else {
      filter = this.filters[viewName];
      this.copyColumnVisibility(filter.columns, cols);
      filter.rows = dataSet;
      filter.columns = cols;
      filter.allSkills = allSkills;
    }

    this.applyFilter(viewName);
  }

  // Register a filter
  unregisterFilter() {}

  getFilter(viewName) {
    return this.filters[viewName];
  }

  // Called when an indicator is added / removed
  notifyIndicatorChange(viewName, indicatorLabel, selected) {
    if (isDefined(this.filters[viewName])) {
      let currentFilter = this.filters[viewName];
      if (currentFilter.indicators.hasOwnProperty(indicatorLabel)) {
        currentFilter.indicators[indicatorLabel] = selected;
      }
      this.applyFilter(viewName);
      this.saveFilter(viewName);
    }
  }

  // Called when the user changes the ledia
  notifyMediaChange(viewName, mediaAccessor, applyFilterNow) {
    if (isDefined(this.filters[viewName])) {
      this.filters[viewName].mediaAccessor = mediaAccessor;
      if (isUndefined(applyFilterNow) || applyFilterNow) {
        this.applyFilter(viewName);
      }
    }
  }

  // Called when the domain changes
  notifyDomainChange(viewName, domainUuids) {
    if (isDefined(this.filters[viewName])) {
      this.filters[viewName].domainUuids = domainUuids;
      this.applyFilter(viewName);
      this.saveFilter(viewName);
    }
  }

  // Called when the skill changes to an other media
  notifySkillChange(viewName, skillUuids, dontSave = true) {
    if (isDefined(this.filters[viewName])) {
      this.filters[viewName].skillUuids = skillUuids;
      this.applyFilter(viewName);
      if (!dontSave) {
        this.saveFilter(viewName);
      }
    }
  }

  isFilterActive(viewName) {
    let filter = this.getFilter(viewName);
    let key;
    if (isDefined(filter)) {
      for (key in filter.indicators) {
        if (!filter.indicators[key]) {
          return true;
        }
      }
      return (
        filter.domainUuids.length !== filter.allDomains.length ||
        filter.skillUuids.length !== filter.allSkills.length ||
        Object.keys(filter.indicators).length !== this.SKILL_METRIC_NAMES.length
      );
    }
    return false;
  }
}

export default 'skillFilterHypervision';
